import { isDeactivated, isDeleted } from '@shared/helpers/element.helpers';
import { isElementActiveInProposal } from '@shared/helpers/proposal.helpers';
import {
  getChildElements,
  isBuildingVersionElement,
  type ElementPath,
} from '@shared/helpers/recursive-element.helpers';
import type { OneOfElements } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';

/**
 * Children that contribute to {@link element}'s rollup.
 *
 * An element that is itself active in the proposal follows proposal membership for its
 * children. An inactive element (deactivated or removed by the proposal) instead keeps
 * its full non-deactivated subtree, so callers can still show what its subtree would
 * contribute if it were re-activated.
 *
 * Shared by the compute pipeline (`buildResultsRecordFromProposalComputeRecord`) and the
 * conservative-carbon backfill so both roll up the exact same set of children.
 *
 * @param element - Parent whose children are being rolled up.
 * @param elementPath - Path ending at {@link element} itself, e.g. `[version, ...ancestors, element]`.
 * @param proposal - Proposal being rolled up, or `undefined` for the baseline.
 */
export const getVisibleChildren = (
  element: OneOfElements,
  elementPath: ElementPath,
  proposal: IProposal | undefined,
): OneOfElements[] => {
  // A version is always "active"; for elements, membership is read from the parent path.
  const isActiveInProposal =
    isBuildingVersionElement(element) ||
    isElementActiveInProposal(proposal, elementPath.slice(0, -1), element);

  return getChildElements(element).filter((child) =>
    isActiveInProposal
      ? isElementActiveInProposal(proposal, elementPath, child)
      : !isDeactivated(child, false) && !isDeleted(child),
  );
};
